import React, { useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { OWNER_UID } from '../constants/owner';

type FloatingAddButtonMode =
  | 'direct-upload'  // 直接ファイル選択 (UserPageなど)
  | 'custom';        // カスタムハンドラー

interface UnifiedFloatingAddButtonProps {
  mode: FloatingAddButtonMode;

  // direct-uploadモードの場合
  onImageSelect?: (file: File) => void;

  // customモードの場合
  onCustomClick?: () => void;
  customTitle?: string;
  customIcon?: React.ReactNode;

  // 共通オプション
  className?: string;
  disabled?: boolean;
}

const UnifiedFloatingAddButton: React.FC<UnifiedFloatingAddButtonProps> = ({
  mode,
  onImageSelect,
  onCustomClick,
  customTitle,
  customIcon,
  className = '',
  disabled = false
}) => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (files: FileList | null) => {
    if (files && files[0]) {
      if (files[0].type.startsWith('image/')) {
        onImageSelect?.(files[0]);
      } else {
        alert("Please select an image file.");
      }
    }
  };

  const isOwner = currentUser?.id === OWNER_UID;

  const handleClick = () => {
    if (disabled || !isOwner) return;

    switch (mode) {
      case 'direct-upload':
        fileInputRef.current?.click();
        break;

      case 'custom':
        onCustomClick?.();
        break;
    }
  };

  // オーナー以外には何も表示しない
  if (!isOwner) return null;

  const getTitle = () => {
    if (customTitle) return customTitle;

    switch (mode) {
      case 'direct-upload':
        return "Add new photo";
      case 'custom':
        return "Action";
      default:
        return "";
    }
  };

  const renderIcon = () => {
    if (customIcon) return customIcon;

    // デフォルトの+アイコン
    return (
      <svg
        className="w-7 h-7 md:w-9 md:h-9 lg:w-9 lg:h-9"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  };

  return (
    <>
      {/* direct-uploadモードでのみファイル入力 */}
      {mode === 'direct-upload' && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFileChange(e.target.files)}
        />
      )}

      {/* 統一された+ボタン */}
      <div className={`fixed bottom-6 right-6 ${className || 'z-40'}`}>
        <button
          onClick={handleClick}
          disabled={disabled}
          className={`bg-[#0DD699] hover:bg-[#3DDDA8] text-black p-4 md:p-6 lg:p-6 rounded-full shadow-lg transition-all duration-300 hover:scale-105 active:scale-95 ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          title={getTitle()}
        >
          {renderIcon()}
        </button>
      </div>
    </>
  );
};

export default UnifiedFloatingAddButton;
